import { resourcesApi, type Resource } from '../services/resources-api';
import { api, type AISearchResult } from '../services/api';

// Local types for search results
interface SearchResult {
  id: string;
  docId?: string;
  type: 'knowledge' | 'multimedia' | 'notes';
  title: string;
  excerpt?: string;
  chunkText?: string;
  score: number;
  source?: string;
  videoUrl?: string;
  thumbnailUrl?: string;
  views?: number;
  contentFormat?: string;
}

type SearchType = 'all' | 'knowledge' | 'multimedia';

export type OnResultSelectCallback = (docId: string, source: string, type: 'knowledge' | 'multimedia' | 'notes') => void;
export type OnAskClickCallback = (query: string) => void;
export type OnMindMapClickCallback = (query: string) => void;

export type SearchMode = 'normal' | 'ai';

export class KnowledgeSearch {
  private modalEl: HTMLElement;
  private inputEl: HTMLInputElement;
  private resultsEl: HTMLElement;
  private filterTypeEl: HTMLSelectElement;
  private filterCategoryEl: HTMLSelectElement;
  private filterCollectionEl: HTMLSelectElement;
  private searchModeEl: HTMLSelectElement | null = null;
  private isOpen: boolean = false;
  private searchTimeout: number | null = null;
  private results: SearchResult[] = [];
  private aiResult: AISearchResult | null = null;
  private searchMode: SearchMode = 'normal';
  private selectedIndex: number = -1;
  private onResultSelect: OnResultSelectCallback | null = null;
  private onAskClick: OnAskClickCallback | null = null;
  private onMindMapClick: OnMindMapClickCallback | null = null;

  constructor() {
    this.createModal();
    this.modalEl = document.getElementById('knowledge-search-modal')!;
    this.inputEl = document.getElementById('knowledge-search-input') as HTMLInputElement;
    this.resultsEl = document.getElementById('knowledge-search-results')!;
    this.filterTypeEl = document.getElementById('knowledge-filter-type') as HTMLSelectElement;
    this.filterCategoryEl = document.getElementById('knowledge-filter-category') as HTMLSelectElement;
    this.filterCollectionEl = document.getElementById('knowledge-filter-collection') as HTMLSelectElement;
    this.searchModeEl = document.getElementById('knowledge-search-mode') as HTMLSelectElement;

    this.setupEventListeners();
    this.setupKeyboardShortcut();
  }

  private createModal(): void {
    const modal = document.createElement('div');
    modal.id = 'knowledge-search-modal';
    modal.className = 'knowledge-search-modal hidden';
    modal.innerHTML = `
      <div class="knowledge-search-container">
        <div class="knowledge-search-header">
          <div class="knowledge-search-input-wrapper">
            <span class="knowledge-search-icon">&#128269;</span>
            <input
              type="text"
              id="knowledge-search-input"
              placeholder="Search knowledge base..."
              autocomplete="off"
            />
          </div>
          <div class="knowledge-search-filters">
            <select id="knowledge-search-mode" class="search-mode-select">
              <option value="normal">Normal Search</option>
              <option value="ai">AI Search</option>
            </select>
            <select id="knowledge-filter-type">
              <option value="all">All Content</option>
              <option value="knowledge">Knowledge</option>
              <option value="multimedia">Multimedia</option>
            </select>
            <select id="knowledge-filter-category">
              <option value="">All Categories</option>
              <option value="content-creation">Content Creation</option>
              <option value="business">Business</option>
              <option value="marketing">Marketing</option>
              <option value="paid-ads">Paid Ads</option>
              <option value="desarrollo-personal">Desarrollo Personal</option>
              <option value="programming">Programming</option>
              <option value="video-editing">Video Editing</option>
              <option value="ai-prompts">AI Prompts</option>
              <option value="hyperfocus-dopamine">Hyperfocus/Dopamine</option>
              <option value="adsconcierge">AdsConcierge</option>
              <option value="site-projects">Site Projects</option>
            </select>
            <select id="knowledge-filter-collection">
              <option value="">All Collections</option>
              <option value="general">General</option>
              <option value="journaling">Journaling</option>
              <option value="newsletters">Newsletters</option>
              <option value="my-voice">My Voice</option>
              <option value="content-reference">Content Reference</option>
              <option value="meetings">Meetings</option>
              <option value="therapy">Therapy</option>
            </select>
          </div>
        </div>
        <div id="knowledge-search-results" class="knowledge-search-results">
          <div class="knowledge-search-hint">
            Type to search your knowledge base...
          </div>
        </div>
        <div class="knowledge-search-footer">
          <div class="knowledge-search-footer-left">
            <span><kbd>&#8593;</kbd><kbd>&#8595;</kbd> Navigate</span>
            <span><kbd>Enter</kbd> Select</span>
            <span><kbd>Esc</kbd> Close</span>
          </div>
          <div class="knowledge-search-footer-actions">
            <button id="knowledge-search-mindmap-btn" class="knowledge-action-btn" title="Generate mind map">
              Mind Map
            </button>
            <button id="knowledge-search-ask-btn" class="knowledge-action-btn" title="Ask a question">
              Ask
            </button>
          </div>
        </div>
      </div>
    `;
    document.body.appendChild(modal);
  }

  private setupEventListeners(): void {
    // Close on backdrop click
    this.modalEl.addEventListener('click', (e) => {
      if (e.target === this.modalEl) {
        this.close();
      }
    });

    // Search input
    this.inputEl.addEventListener('input', () => {
      if (this.searchTimeout) {
        window.clearTimeout(this.searchTimeout);
      }

      this.searchTimeout = window.setTimeout(() => {
        this.performSearch();
      }, 300);
    });

    // Keyboard navigation
    this.inputEl.addEventListener('keydown', (e) => {
      switch (e.key) {
        case 'ArrowDown':
          e.preventDefault();
          this.selectNext();
          break;
        case 'ArrowUp':
          e.preventDefault();
          this.selectPrevious();
          break;
        case 'Enter':
          e.preventDefault();
          this.selectCurrent();
          break;
        case 'Escape':
          e.preventDefault();
          this.close();
          break;
      }
    });

    // Search mode change
    this.searchModeEl?.addEventListener('change', () => {
      this.searchMode = this.searchModeEl!.value as SearchMode;
      this.performSearch();
    });

    // Filter changes
    this.filterTypeEl.addEventListener('change', () => {
      this.performSearch();
    });

    this.filterCategoryEl.addEventListener('change', () => {
      this.performSearch();
    });

    this.filterCollectionEl.addEventListener('change', () => {
      this.performSearch();
    });

    // Ask button
    document.getElementById('knowledge-search-ask-btn')?.addEventListener('click', () => {
      const query = this.inputEl.value.trim();
      if (this.onAskClick) {
        this.close();
        this.onAskClick(query);
      }
    });

    // Mind Map button
    document.getElementById('knowledge-search-mindmap-btn')?.addEventListener('click', () => {
      const query = this.inputEl.value.trim();
      if (this.onMindMapClick && query) {
        this.close();
        this.onMindMapClick(query);
      }
    });
  }

  private setupKeyboardShortcut(): void {
    document.addEventListener('keydown', (e) => {
      // Ctrl+K or Cmd+K to open
      if ((e.ctrlKey || e.metaKey) && e.key === 'k') {
        e.preventDefault();
        if (this.isOpen) {
          this.close();
        } else {
          this.open();
        }
      }
    });
  }

  private async performSearch(): Promise<void> {
    const query = this.inputEl.value.trim();

    if (!query) {
      this.resultsEl.innerHTML = '<div class="knowledge-search-hint">Type to search your knowledge base...</div>';
      this.results = [];
      this.aiResult = null;
      this.selectedIndex = -1;
      return;
    }

    this.resultsEl.innerHTML = '<div class="knowledge-search-loading">Searching...</div>';

    try {
      if (this.searchMode === 'ai') {
        // AI Search - uses AI Search (AutoRAG)
        await this.performAISearch(query);
      } else {
        // Normal search - uses Vectorize
        await this.performNormalSearch(query);
      }
    } catch (error) {
      this.resultsEl.innerHTML = '<div class="knowledge-search-error">Search failed</div>';
    }
  }

  private async performNormalSearch(query: string): Promise<void> {
    const searchType = this.filterTypeEl.value as SearchType;

    const response = await resourcesApi.search(query, {
      type: searchType !== 'all' ? searchType : undefined,
      limit: 15
    });

    if (response.success && response.resources) {
      // Convert Resource[] to SearchResult[]
      this.results = response.resources.map(r => ({
        id: r.id,
        type: r.type as 'knowledge' | 'multimedia' | 'notes',
        title: r.title,
        excerpt: r.content?.slice(0, 150) || undefined,
        score: 1, // D1 search doesn't return scores
        source: r.source || undefined,
        videoUrl: (r.metadata as Record<string, unknown>)?.videoUrl as string | undefined,
        contentFormat: (r.customFields as Record<string, unknown>)?.contentFormat as string | undefined
      }));
      this.aiResult = null;
      this.selectedIndex = this.results.length > 0 ? 0 : -1;
      this.renderResults();
    } else {
      this.resultsEl.innerHTML = '<div class="knowledge-search-error">Search failed</div>';
    }
  }

  private async performAISearch(query: string): Promise<void> {
    const response = await api.aiSearch(query, { maxResults: 10 });

    if (response.success && response.data) {
      this.aiResult = response.data;
      this.results = [];
      this.selectedIndex = -1;
      this.renderAIResults();
    } else {
      this.resultsEl.innerHTML = '<div class="knowledge-search-error">AI Search failed. Make sure the index is configured.</div>';
    }
  }

  private renderAIResults(): void {
    if (!this.aiResult) {
      this.resultsEl.innerHTML = '<div class="knowledge-search-empty">No results</div>';
      return;
    }

    const sourcesHtml = this.aiResult.sources.map(source => `
      <div class="ai-search-source">
        <span class="ai-source-bucket">${source.bucket}</span>
        <span class="ai-source-file">${source.filename}</span>
        <span class="ai-source-score">${Math.round(source.score * 100)}%</span>
      </div>
    `).join('');

    this.resultsEl.innerHTML = `
      <div class="ai-search-result">
        <div class="ai-search-answer">
          <div class="ai-answer-label">AI Answer</div>
          <div class="ai-answer-text">${this.escapeHtml(this.aiResult.answer)}</div>
        </div>
        <div class="ai-search-sources">
          <div class="ai-sources-label">Sources (${this.aiResult.sources.length})</div>
          ${sourcesHtml}
        </div>
        <div class="ai-search-cost-hint">
          AI Search uses more tokens than normal search
        </div>
      </div>
    `;
  }

  private escapeHtml(text: string): string {
    const div = document.createElement('div');
    div.textContent = text;
    return div.innerHTML;
  }

  private renderResults(): void {
    if (this.results.length === 0) {
      this.resultsEl.innerHTML = '<div class="knowledge-search-empty">No results found</div>';
      return;
    }

    this.resultsEl.innerHTML = this.results.map((result, index) => {
      const scorePercent = Math.round(result.score * 100);
      const isSelected = index === this.selectedIndex;

      if (result.type === 'multimedia') {
        return this.renderMultimediaResult(result, index, isSelected, scorePercent);
      } else {
        return this.renderKnowledgeResult(result, index, isSelected, scorePercent);
      }
    }).join('');

    // Add click handlers
    this.resultsEl.querySelectorAll('.knowledge-search-result').forEach((el) => {
      el.addEventListener('click', () => {
        const index = parseInt(el.getAttribute('data-index') || '0');
        this.selectedIndex = index;
        this.selectCurrent();
      });
    });
  }

  private renderKnowledgeResult(result: SearchResult, index: number, isSelected: boolean, scorePercent: number): string {
    const sourceBadge = this.getSourceBadge(result.source || 'unknown');

    return `
      <div class="knowledge-search-result ${isSelected ? 'selected' : ''}" data-index="${index}" data-type="knowledge">
        <div class="knowledge-result-header">
          <span class="knowledge-result-title">${result.title}</span>
          <span class="knowledge-result-score">${scorePercent}%</span>
        </div>
        <div class="knowledge-result-excerpt">
          ${result.excerpt || result.chunkText?.slice(0, 150) + '...' || ''}
        </div>
        <div class="knowledge-result-meta">
          ${sourceBadge}
          ${result.videoUrl ? '<span class="knowledge-video-badge">📹 Video</span>' : ''}
        </div>
      </div>
    `;
  }

  private renderMultimediaResult(result: SearchResult, index: number, isSelected: boolean, scorePercent: number): string {
    const sourceBadge = this.getSourceBadge(result.source || 'web');
    const thumbnail = result.thumbnailUrl
      ? `<img src="${result.thumbnailUrl}" alt="" class="knowledge-result-thumbnail" />`
      : '';
    const viewsBadge = result.views ? `<span class="knowledge-views-badge">👁 ${this.formatViews(result.views)}</span>` : '';
    const formatBadge = result.contentFormat ? `<span class="knowledge-format-badge">${result.contentFormat}</span>` : '';

    return `
      <div class="knowledge-search-result multimedia ${isSelected ? 'selected' : ''}" data-index="${index}" data-type="multimedia">
        <div class="knowledge-result-content">
          ${thumbnail}
          <div class="knowledge-result-info">
            <div class="knowledge-result-header">
              <span class="knowledge-result-title">${result.title}</span>
              <span class="knowledge-result-score">${scorePercent}%</span>
            </div>
            <div class="knowledge-result-excerpt">
              ${result.excerpt || ''}
            </div>
            <div class="knowledge-result-meta">
              <span class="knowledge-type-badge multimedia">Multimedia</span>
              ${sourceBadge}
              ${formatBadge}
              ${viewsBadge}
            </div>
          </div>
        </div>
      </div>
    `;
  }

  private formatViews(views: number): string {
    if (views >= 1000000) return (views / 1000000).toFixed(1) + 'M';
    if (views >= 1000) return (views / 1000).toFixed(1) + 'K';
    return views.toString();
  }

  private getSourceBadge(source: string): string {
    const sourceColors: Record<string, string> = {
      notepad: '#4CAF50',
      gdrive: '#4285f4',
      youtube: '#FF0000',
      n8n: '#FF6D5A',
      omi: '#9C27B0',
      voicenotes: '#00BCD4',
      hidoc: '#FF9800',
      browser: '#607D8B',
      // Multimedia sources
      instagram: '#E1306C',
      tiktok: '#000000',
      web: '#607D8B'
    };

    const color = sourceColors[source] || '#666';
    return `<span class="knowledge-source-badge" style="background: ${color}">${source}</span>`;
  }

  private selectNext(): void {
    if (this.results.length === 0) return;
    this.selectedIndex = (this.selectedIndex + 1) % this.results.length;
    this.renderResults();
    this.scrollToSelected();
  }

  private selectPrevious(): void {
    if (this.results.length === 0) return;
    this.selectedIndex = this.selectedIndex <= 0 ? this.results.length - 1 : this.selectedIndex - 1;
    this.renderResults();
    this.scrollToSelected();
  }

  private scrollToSelected(): void {
    const selected = this.resultsEl.querySelector('.knowledge-search-result.selected');
    if (selected) {
      selected.scrollIntoView({ block: 'nearest' });
    }
  }

  private selectCurrent(): void {
    if (this.selectedIndex >= 0 && this.selectedIndex < this.results.length) {
      const result = this.results[this.selectedIndex];
      this.close();

      if (this.onResultSelect) {
        const docId = result.type === 'knowledge' ? (result.docId || result.id) : result.id;
        this.onResultSelect(docId, result.source || 'unknown', result.type);
      }
    }
  }

  public open(): void {
    this.isOpen = true;
    this.modalEl.classList.remove('hidden');
    this.inputEl.value = '';
    this.inputEl.focus();
    this.results = [];
    this.selectedIndex = -1;
    this.resultsEl.innerHTML = '<div class="knowledge-search-hint">Type to search your knowledge base...</div>';
  }

  public close(): void {
    this.isOpen = false;
    this.modalEl.classList.add('hidden');
  }

  public setOnResultSelect(callback: OnResultSelectCallback): void {
    this.onResultSelect = callback;
  }

  public setOnAskClick(callback: OnAskClickCallback): void {
    this.onAskClick = callback;
  }

  public setOnMindMapClick(callback: OnMindMapClickCallback): void {
    this.onMindMapClick = callback;
  }
}
